<template>
    <div>
        <b-modal ref="FacilityBulkDownloadPopup" title="계측기 데이터 일괄 다운로드" size="xl">
            <div class="facility-bulk-download-popup">
                <!-- 다운로드 설정 컨테이너 -->
                <div class="form-container">
                    <div class="download-settings">
                        <!-- 기간 및 조회 유형 설정 -->
                        <h6 class="section-title">수집 데이터 다운로드</h6>
                        <div class="download-setting-content">
                            <div class="period-and-query-section">
                                <div class="download-setting-description">
                                    조회 기간을 지정하여 선택한 계측기의 수집 데이터 또는 집계 데이터를 다운로드할 수
                                    있습니다.<br />
                                    선택된 계측기: {{ selectedFacilities.length }}개
                                </div>
                                <div class="settings-row">
                                    <div class="query-selection">
                                        <label>데이터 유형</label>
                                        <select v-model="selectedTimeDsvn" class="form-control">
                                            <option value="">조회 유형을 선택하세요</option>
                                            <option value="raw">원시 데이터</option>
                                            <option
                                                v-for="timeDsvn in timeDsvn"
                                                :key="timeDsvn.value"
                                                :value="timeDsvn.value"
                                            >
                                                {{ timeDsvn.label }}
                                            </option>
                                        </select>
                                    </div>
                                    <div v-if="selectedTimeDsvn === 'raw'" class="query-selection">
                                        <label>로그 유형</label>
                                        <!-- <small class="form-text text-muted" style="font-size: 11px; margin-top: 4px;">
                                            BEPLOG: 전력량계 원시 데이터, ESPLOG: 환경센서 원시 데이터
                                        </small> -->
                                        <select v-model="selectedLogType" class="form-control">
                                            <option value="">로그 유형을 선택하세요</option>
                                            <option value="BEPLOG">BEP 로그</option>
                                            <option value="ESPLOG">ESP 로그</option>
                                        </select>
                                    </div>
                                    <div class="date-range">
                                        <div class="date-input">
                                            <label>조회 시작</label>
                                            <DatePicker
                                                v-model="fromDate"
                                                :type="datePckkerModel"
                                                value-type="format"
                                                :format="datePickerFormat"
                                                placeholder="시작일을 선택하세요"
                                            />
                                        </div>
                                        <div class="date-input">
                                            <label>조회 종료</label>
                                            <DatePicker
                                                v-model="toDate"
                                                :type="datePckkerModel"
                                                value-type="format"
                                                :format="datePickerFormat"
                                                placeholder="종료일을 선택하세요"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 선택된 계측기 목록 -->
                        <div class="options-section">
                            <div class="option-group">
                                <div class="option-header">
                                    <h6 class="option-title">
                                        <i class="fa fa-list"></i> 선택된 계측기 ({{ selectedFacilities.length }}개)
                                    </h6>
                                </div>
                                <div class="selected-facilities-list">
                                    <div
                                        class="facility-tag"
                                        v-for="facility in selectedFacilities"
                                        :key="facility.facilityIdx || facility.facilityName"
                                    >
                                        {{ facility.facilityName }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="close"> 취소 </b-button>
                <b-button variant="primary" @click="downloadData" :disabled="isDownloading">
                    <i v-if="isDownloading" class="fa fa-spinner fa-spin"></i>
                    <i v-else class="fa fa-download"></i>
                    데이터 다운로드
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { mapState, mapMutations } from "vuex";
import { extractFilenameFromResponse } from "@src/utils/exportExcelTitle.js";

export default {
    components: {
        DatePicker,
    },
    props: {
        selectedFacilities: {
            type: Array,
            default: () => [],
        },
        serverIdx: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            fromDate: this.getDefaultFromDate(),
            toDate: this.getDefaultToDate(),
            timeDsvn: [],
            selectedTimeDsvn: "",
            selectedLogType: "", // BEPLOG 또는 ESPLOG
            isDownloading: false,
            // 집계 데이터 입력 필수 데이터 유효성 조건
            isRequiredSumData: {
                selectedTimeDsvn: {
                    required: true,
                    message: "조회 유형을 선택하세요.",
                },
                fromDate: {
                    required: true,
                    message: "시작일을 선택하세요.",
                },
                toDate: {
                    required: true,
                    message: "종료일을 선택하세요.",
                },
            },
            // 원시 데이터 입력 필수 데이터 유효성 조건
            isRequiredRawData: {
                selectedLogType: {
                    required: true,
                    message: "로그 유형을 선택하세요.",
                },
                fromDate: {
                    required: true,
                    message: "시작일을 선택하세요.",
                },
                toDate: {
                    required: true,
                    message: "종료일을 선택하세요.",
                },
            },
        };
    },
    watch: {
        selectedFacilities: {
            handler(newVal) {
                // 선택된 계측기 변경 시 처리 (필요시)
                console.log("selectedFacilities changed:", newVal);
            },
            immediate: true,
            deep: true,
        },
        selectedTimeDsvn(newVal) {
            // 데이터 유형 변경 시 날짜 포맷을 모드에 맞게 조정
            if (newVal) {
                this.adjustDateFormats(newVal);
            }
        },
    },
    computed: {
        ...mapState({
            commonCodes: (state) => state.commonCodes,
        }),
        facilityIdxs() {
            return this.selectedFacilities.map((facility) => facility.facilityIdx).filter((idx) => idx != null);
        },
        datePckkerModel() {
            if (this.selectedTimeDsvn === "YEAR") {
                return "year";
            } else if (this.selectedTimeDsvn === "MONTH") {
                return "month";
            } else {
                return "date";
            }
        },
        datePickerFormat() {
            if (this.selectedTimeDsvn === "YEAR") {
                return "YYYY";
            } else if (this.selectedTimeDsvn === "MONTH") {
                return "YYYY-MM";
            } else {
                return "YYYY-MM-DD";
            }
        },
    },
    mounted() {
        this.getTimeDsvn();
    },
    methods: {
        ...mapMutations(["SET_IS_LOADING"]),
        setIsLoadingState(status) {
            this.SET_IS_LOADING(status);
        },
        show() {
            // b-modal의 show 메서드 직접 호출
            this.$nextTick(() => {
                if (this.$refs.FacilityBulkDownloadPopup) {
                    this.$refs.FacilityBulkDownloadPopup.show();
                }
            });
        },
        close() {
            // b-modal의 hide 메서드 직접 호출
            if (this.$refs.FacilityBulkDownloadPopup) {
                this.$refs.FacilityBulkDownloadPopup.hide();
            }
        },
        getTimeDsvn() {
            if (!this.commonCodes.timeDsvn) return;

            this.timeDsvn = this.commonCodes.timeDsvn
                .filter((code) => code.options.type === "Type1")
                .map((code) => ({
                    value: code.value,
                    label: code.text,
                }));
        },
        getDefaultFromDate() {
            const date = new Date();
            date.setDate(date.getDate() - 7); // 7일 전
            return this.formatDate(date);
        },
        getDefaultToDate() {
            return this.formatDate(new Date());
        },
        formatDate(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        },
        adjustDateFormats(timeDsvn) {
            // 날짜 값을 현재 모드에 맞게 포맷 조정
            if (timeDsvn === "YEAR") {
                // 연도 모드: YYYY 형식으로 변환
                if (this.fromDate) {
                    const date = new Date(this.fromDate);
                    this.fromDate = String(date.getFullYear());
                }
                if (this.toDate) {
                    const date = new Date(this.toDate);
                    this.toDate = String(date.getFullYear());
                }
            } else if (timeDsvn === "MONTH") {
                // 월 모드: YYYY-MM 형식으로 변환
                if (this.fromDate) {
                    const date = new Date(this.fromDate);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    this.fromDate = `${year}-${month}`;
                }
                if (this.toDate) {
                    const date = new Date(this.toDate);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    this.toDate = `${year}-${month}`;
                }
            } else {
                // 일 모드: YYYY-MM-DD 형식으로 변환
                if (this.fromDate) {
                    const date = new Date(this.fromDate);
                    this.fromDate = this.formatDate(date);
                }
                if (this.toDate) {
                    const date = new Date(this.toDate);
                    this.toDate = this.formatDate(date);
                }
            }
        },
        // 통합된 유효성 검사 메서드
        validateData(requiredConfig) {
            const errors = [];

            Object.entries(requiredConfig).forEach(([key, config]) => {
                if (config.required) {
                    let isValid = true;

                    if (key in this) {
                        const value = this[key];
                        if (Array.isArray(value)) {
                            isValid = value.length > 0;
                        } else {
                            isValid = !!value;
                        }
                    } else {
                        isValid = false;
                    }

                    if (!isValid) {
                        errors.push(config.message);
                    }
                }
            });

            return {
                isValid: errors.length === 0,
                errors: errors,
            };
        },
        dateValidation() {
            // 모드에 따라 날짜 파싱 방식 조정
            let fromDate, toDate;

            if (this.selectedTimeDsvn === "YEAR") {
                // 연도 모드: YYYY 형식
                fromDate = new Date(`${this.fromDate}-01-01`);
                toDate = new Date(`${this.toDate}-12-31`);
            } else if (this.selectedTimeDsvn === "MONTH") {
                // 월 모드: YYYY-MM 형식
                fromDate = new Date(`${this.fromDate}-01`);
                const toDateParts = this.toDate.split("-");
                const lastDay = new Date(toDateParts[0], toDateParts[1], 0).getDate();
                toDate = new Date(`${this.toDate}-${lastDay}`);
            } else {
                // 일 모드: YYYY-MM-DD 형식
                fromDate = new Date(this.fromDate);
                toDate = new Date(this.toDate);
            }

            const today = new Date();
            today.setHours(23, 59, 59, 999); // 오늘 끝까지

            if (fromDate > toDate) {
                this.alertWarning("시작일이 종료일보다 클 수 없습니다.");
                return false;
            }

            return true;
        },
        async downloadData() {
            // 날짜 유효성 검사
            if (!this.dateValidation()) {
                return;
            }

            // facilityIdxs 확인
            if (!this.facilityIdxs || this.facilityIdxs.length === 0) {
                this.alertWarning("다운로드할 계측기를 선택해주세요.");
                return;
            }

            // 원시 데이터인지 집계 데이터인지 확인
            const isRawData = this.selectedTimeDsvn === "raw";
            let dataType = isRawData ? "원시데이터" : "집계데이터";

            // 유효성 검사 실행
            const validation = this.validateData(isRawData ? this.isRequiredRawData : this.isRequiredSumData);
            if (!validation.isValid) {
                this.alertWarning(validation.errors.join("\n"));
                return;
            }

            try {
                this.isDownloading = true;
                this.setIsLoadingState(true);
                let response;

                if (isRawData) {
                    // 원시 데이터는 로그 유형(BEPLOG/ESPLOG)을 timeDsvn으로 사용
                    this.alertNoti("다운로드가 백그라운드에서 실행됩니다.");
                    response = await backEndApi.facility.downloadRawDataByFacilityBulk(
                        this.facilityIdxs,
                        this.selectedLogType, // BEPLOG 또는 ESPLOG를 timeDsvn으로 사용
                        this.fromDate,
                        this.toDate,
                        [] // propNames는 빈 배열로 전달 (모든 측정 항목 포함)
                    );
                } else {
                    this.alertNoti("다운로드가 백그라운드에서 실행됩니다.");
                    response = await backEndApi.facility.downloadSumDataByFacilityBulk(
                        this.facilityIdxs,
                        this.selectedTimeDsvn,
                        this.fromDate,
                        this.toDate,
                        [] // propNames는 빈 배열로 전달 (모든 측정 항목 포함)
                    );
                }

                this.handleDownloadResponse(response, dataType);
            } catch (error) {
                console.error("Download error:", error);
                this.alertWarning(error.detail || error.message || "다운로드 중 오류가 발생했습니다.");
            } finally {
                this.isDownloading = false;
                this.setIsLoadingState(false);
            }
        },
        handleDownloadResponse(response, dataType) {
            if (response && response.data) {
                const blob = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=utf-8",
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;

                const fileName = extractFilenameFromResponse(
                    response,
                    `${dataType}_${this.fromDate}_${this.toDate}.xlsx`
                );

                link.download = fileName;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                window.URL.revokeObjectURL(url);

                this.alertNoti("다운로드가 완료되었습니다.");
                this.close();
            }
        },
    },
};
</script>

<style scoped>
.facility-bulk-download-popup {
    padding: 10px;
}

.download-setting-description {
    font-size: 12px;
    color: #6c757d;
    margin-bottom: 10px;
    line-height: 1.5;
}

.download-setting-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    gap: 20px;
}

.facility-bulk-download-popup {
    margin: 0 auto;
}

.form-container {
    background: #ffffff;
}

.section-title {
    margin-bottom: 10px;
    padding: 8px 0;
    color: #495057;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.period-and-query-section {
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #dee2e6;
    flex: 1;
    min-width: 0;
}

.settings-row {
    display: flex;
    gap: 20px;
    align-items: flex-end;
}

.date-range {
    display: flex;
    gap: 15px;
    flex: 2;
}

.date-input {
    flex: 1;
}

.date-input label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #495057;
    font-size: 12px;
}

.query-selection {
    flex: 1;
    min-width: 200px;
}

.query-selection label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #495057;
    font-size: 12px;
}

.options-section {
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
    margin-top: 15px;
}

.option-group {
    margin-bottom: 20px;
    padding: 15px;
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
}

.option-group:last-child {
    margin-bottom: 0;
}

.option-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e9ecef;
}

.option-title {
    margin: 0;
    color: #495057;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
}

.option-title i {
    color: #6c757d;
    font-size: 12px;
}

.prop-actions {
    display: flex;
    gap: 8px;
}

.prop-checkbox-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    border-radius: 10px;
}

.prop-checkbox-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 8px 10px;
    border-radius: 8px;
    transition: all 0.3s ease;
    border: 2px solid #e9ecef;
    background: #ffffff;
    position: relative;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.prop-checkbox-label:hover {
    background-color: #f8f9fa;
    border-color: #007bff;
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.15);
    transform: translateY(-1px);
}

.prop-checkbox-label:has(input:checked) {
    border-color: #28a745;
    background-color: #f8fff9;
    box-shadow: 0 4px 8px rgba(40, 167, 69, 0.15);
}

.prop-checkbox-label input[type="checkbox"] {
    margin-right: 10px;
    transform: scale(1.3);
    accent-color: #28a745;
    cursor: pointer;
}

.prop-name {
    font-weight: 500;
    color: #2c3e50;
    font-size: 12px;
}

.selected-facilities-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;
    padding: 10px;
}

.facility-tag {
    padding: 6px 12px;
    background: #e3f2fd;
    border: 1px solid #90caf9;
    border-radius: 16px;
    font-size: 12px;
    color: #1976d2;
    font-weight: 500;
}

.form-control {
    border-radius: 6px;
    border: 1px solid #ced4da;
    padding: 6px 10px;
    transition: border-color 0.2s;
    font-size: 13px;
}

.form-control:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* DatePicker 커스텀 스타일 */
.mx-datepicker {
    width: 100%;
}

.mx-input {
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 6px 10px;
    transition: border-color 0.2s;
    background-color: #fff;
    font-size: 13px;
}

.mx-input:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
}

.no-props-message {
    padding: 20px;
    text-align: center;
    color: #6c757d;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.no-props-message i {
    font-size: 16px;
    color: #adb5bd;
}
</style>
